/**
 * @file StatementIntegrityBadge.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-11-03
 * @description The "Xero check" cell in the statement approval table: whether a
 *              generated statement's figures were proved against this system's
 *              data and against Xero. Loud only when it matters — a red pill
 *              for a mismatch, amber for a note — and a quiet tick otherwise.
 *              Clicking it opens StatementIntegrityModal (the caller's onClick).
 *              1.2.0: a statement right as at its period end that has changed
 *              since (credits allocated after it) reads "Changed since", amber.
 *              1.3.0 (2026-10-20, the statements look): solid badges at full
 *              opacity with dark text (StatementRun.css's recipe) and an icon
 *              each, so the colour is never the only signal.
 *              1.4.0 (2026-11-03): a statement whose only difference with
 *              Xero is invoices approved here, not yet in Xero, reads "Held
 *              here", amber (heldHereOf).
 */

import React from 'react';
import './StatementIntegrityBadge.css';
import { effectiveIntegrityStatus, changedSinceOf, CHANGED_SINCE_LABEL, heldHereOf, HELD_HERE_LABEL } from '../utils/statementIntegrity';

function StatementIntegrityBadge({ integrity, integrityAvailable = true, integrityReason = null, onClick, periodEndLabel }) {
  if (!integrityAvailable) {
    const why = integrityReason || 'Not ready — migration statement_reconciliation.sql has not run';
    return (
      <span className="stmt-integrity-badge muted" title={why}>
        Not checked
      </span>
    );
  }

  const status = effectiveIntegrityStatus(integrity);
  const headline = integrity?.headline || '';
  const cleared = integrity?.approvalCleared ? ' · its approval was taken off by the Xero check' : '';

  let content;
  let className = 'stmt-integrity-badge';
  let title = headline;

  switch (status) {
    case 'mismatch':
      className += ' pill mismatch';
      content = (
        <>
          <span className="material-symbols-rounded" aria-hidden="true">error</span>
          <span>{integrity?.acknowledged ? 'Mismatch · acknowledged' : 'Mismatch'}</span>
        </>
      );
      title = `${headline || 'The statement does not match'}${cleared}`;
      break;
    case 'advisory':
      className += ' pill advisory';
      if (heldHereOf(integrity)) {
        // Invoices approved here, not yet in Xero — all of its difference (3 Nov 2026).
        content = <><span className="material-symbols-rounded" aria-hidden="true">cloud_sync</span><span>{HELD_HERE_LABEL}</span></>;
        title = headline || 'Includes invoices approved here, not yet in Xero';
      } else if (changedSinceOf(integrity)) {
        content = <><span className="material-symbols-rounded" aria-hidden="true">info</span><span>{CHANGED_SINCE_LABEL}</span></>;
        title = `${headline || 'Right as at the period end; changed since'} (optional)`;
      } else {
        content = <><span className="material-symbols-rounded" aria-hidden="true">info</span><span>Note</span></>;
        title = headline || 'The closing figure matches; see the note';
      }
      break;
    case 'error':
      className += ' error';
      content = (
        <>
          <span className="material-symbols-rounded">sync_problem</span>
          <span>Not fully checked — retry</span>
        </>
      );
      title = headline || 'The check could not finish';
      break;
    case 'interrupted':
      className += ' error';
      content = (
        <>
          <span className="material-symbols-rounded">sync_problem</span>
          <span>Not fully checked — retry</span>
        </>
      );
      title = 'Check interrupted';
      break;
    case 'pending':
      className += ' pending';
      content = (
        <>
          <span className="material-symbols-rounded stmt-integrity-spin">progress_activity</span>
          <span>Checking…</span>
        </>
      );
      title = 'Comparing with Xero';
      break;
    case 'match':
      className += ' match';
      content = <span className="material-symbols-rounded" aria-label="Matches Xero">check</span>;
      title = `Matches Xero${periodEndLabel ? ` as at ${periodEndLabel}` : ''}${integrity?.reportConfirmed ? ' · confirmed by Xero’s aged report' : ''}`;
      break;
    default:
      className += ' muted';
      content = 'Not checked';
      title = 'Not checked against Xero yet';
      break;
  }

  if (!onClick) {
    return <span className={className} title={title}>{content}</span>;
  }

  return (
    <button
      type="button"
      className={`${className} as-button`}
      title={title}
      aria-label={`Xero check: ${title}`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {content}
    </button>
  );
}

export default StatementIntegrityBadge;
